import React, { useState } from 'react';
import { RealtimeQueryProvider } from './RealtimeQueryProvider';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { logger } from '../../utils/logger';

// ██╗    ██╗ █████╗ ██████╗ ███╗   ██╗██╗███╗   ██╗ ██████╗     ██████╗  ██████╗     ███╗   ██╗ ██████╗ ████████╗    ███╗   ███╗ ██████╗ ██████╗ ██╗███████╗██╗   ██╗
// ██║    ██║██╔══██╗██╔══██╗████╗  ██║██║████╗  ██║██╔════╝     ██╔══██╗██╔═══██╗    ████╗  ██║██╔═══██╗╚══██╔══╝    ████╗ ████║██╔═══██╗██╔══██╗██║██╔════╝╚██╗ ██╔╝
// ██║ █╗ ██║███████║██████╔╝██╔██╗ ██║██║██╔██╗ ██║██║  ███╗    ██║  ██║██║   ██║    ██╔██╗ ██║██║   ██║   ██║       ██╔████╔██║██║   ██║██║  ██║██║█████╗  ╚████╔╝
// ██║███╗██║██╔══██║██╔══██╗██║╚██╗██║██║██║╚██╗██║██║   ██║    ██║  ██║██║   ██║    ██║╚██╗██║██║   ██║   ██║       ██║╚██╔╝██║██║   ██║██║  ██║██║██╔══╝   ╚██╔╝
// ╚███╔███╔╝██║  ██║██║  ██║██║ ╚████║██║██║ ╚████║╚██████╔╝    ██████╔╝╚██████╔╝    ██║ ╚████║╚██████╔╝   ██║       ██║ ╚═╝ ██║╚██████╔╝██████╔╝██║██║       ██║
//  ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝╚═╝  ╚═══╝ ╚═════╝     ╚═════╝  ╚═════╝     ╚═╝  ╚═══╝ ╚═════╝    ╚═╝       ╚═╝     ╚═╝ ╚═════╝ ╚═════╝ ╚═╝╚═╝       ╚═╝

/**
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * DO NOT MODIFY THIS QUERY CLIENT INSTANTIATION!
 *
 * This QueryClient instance is the primary one used throughout the application!
 * There is another instance in src/lib/react-query.ts which is used by offline-sync.ts.
 *
 * BOTH INSTANCES MUST REMAIN as they are until explicitly addressed by Edward Silhol.
 * Changing either will break critical application functionality.
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */
// Create a client with full configuration from our original implementation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for 5 minutes
      gcTime: 24 * 60 * 60 * 1000, // 24 hours - inactive cache data expires after 24 hours
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 2,
    },
  },
});

interface ReactQueryProviderProps {
  children: React.ReactNode;
}

/**
 * Error boundary component for React Query errors
 */
class QueryErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (error: Error) => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI for query errors
      return (
        <div className="p-4 text-red-600 bg-red-100 rounded">
          <h3 className="font-medium">Something went wrong with data fetching</h3>
          <p className="text-sm">Try refreshing the application</p>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Provider component that wraps the application with React Query context
 * and includes DevTools in development mode with error handling
 */
export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
  const [showDevTools] = useState(process.env.NODE_ENV === 'development');

  const handleQueryError = (error: Error) => {
    logger.error('React Query error caught by error boundary:', error);
    // Could add notification display here
  };

  // Create a persister for localStorage with serialization support
  // This allows for potential compression or custom serialization in the future
  const persister = createSyncStoragePersister({
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    serialize: data => JSON.stringify(data),
    deserialize: data => JSON.parse(data),
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
      }}
      onSuccess={() => {
        logger.store('Query cache successfully restored');
      }}
      onError={error => {
        logger.error('Error restoring query cache:', error);
      }}
    >
      <QueryErrorBoundary onError={handleQueryError}>
        <RealtimeQueryProvider>
          {children}

          {/* Only show devtools in development */}
          {showDevTools && <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />}
        </RealtimeQueryProvider>
      </QueryErrorBoundary>
    </PersistQueryClientProvider>
  );
}
